import { useEffect } from 'react';

/**
 * `data-count-to` を持つ要素を、画面に入ったときに 0 から数え上げる。
 * 到達しなかった場合に備え、一定時間で最終値を書き込む安全網を置く。
 */
export default function useCountUp(rootRef, enabled = true) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll('[data-count-to]'));
    if (targets.length === 0) return undefined;

    const finalValue = (el) => Number(el.dataset.countTo).toLocaleString();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || reduce) {
      targets.forEach((el) => { el.textContent = finalValue(el); });
      return undefined;
    }

    targets.forEach((el) => { el.textContent = '0'; });

    const frames = new Map();
    const run = (el) => {
      const to = Number(el.dataset.countTo);
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 900);
        el.textContent = Math.round(to * (1 - (1 - k) ** 3)).toLocaleString();
        if (k < 1) frames.set(el, requestAnimationFrame(step));
      };
      frames.set(el, requestAnimationFrame(step));
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          run(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.6 }
    );
    targets.forEach((el) => io.observe(el));

    const timer = setTimeout(() => {
      targets.forEach((el) => {
        if (el.textContent === '0') el.textContent = finalValue(el);
      });
    }, 4000);

    return () => {
      clearTimeout(timer);
      io.disconnect();
      frames.forEach((id) => cancelAnimationFrame(id));
    };
  }, [rootRef, enabled]);
}
